import * as React from "react";
import { useEffect, useState } from "react";
import { FieldValues, UseControllerProps, useWatch } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import type { SxProps } from "@mui/material/styles";

import { useAppSelector } from "app/hooks";
import AutocompleteFormField from "components/autocomplete-form-field";
import {
  selectCurrentClinic,
  selectCurrentPractice
} from "features/auth/auth-slice";
import PolicyInsurerListItem from "features/patients/sections/patient-insurance-policies/components/policy-insurer-field/policy-insurer-list-item.component";
import { useDebounce } from "hooks/";
import { useGetInsurersQuery } from "services/insurers";
import { Insurer } from "services/types/insurers.type";

type SelectInsurerProps = {
  isActive?: boolean;
  label?: string;
  multiple?: boolean;
  enablePortal?: boolean;
  onOptionSelected?: (option: { label: string; value: string } | null) => void;
  sx?: SxProps;
};

export type RhfSelectInsurer<T extends FieldValues> = SelectInsurerProps &
  UseControllerProps<T>;

const DEFAULT_COUNTRY = "United States of America";

export default function SelectInsurer<T extends FieldValues>({
  control,
  name,
  multiple,
  onOptionSelected,
  label = "By Insurer",
  sx,
  isActive,
  enablePortal
}: RhfSelectInsurer<T>) {
  const currentPractice = useAppSelector(selectCurrentPractice);

  const currentClinicCountry =
    useAppSelector(selectCurrentClinic)?.country || DEFAULT_COUNTRY;

  const [showInsurersCreatedByClinic, setShowInsurersCreatedByClinic] =
    useState(false);

  useEffect(() => {
    setShowInsurersCreatedByClinic(
      Boolean(currentPractice?.billing_use_practice_insurers_only)
    );
  }, [currentPractice?.billing_use_practice_insurers_only]);

  const insurerOption = useWatch({ control, name }) as {
    name: string;
    id: string;
  };

  const [search, setSearch] = useState<string>("");

  React.useEffect(() => {
    setSearch(insurerOption?.name ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { value: dSearch } = useDebounce(search);
  const { data: insurers = [], isLoading } = useGetInsurersQuery({
    search: dSearch,
    is_active: isActive,
    country: currentClinicCountry,
    verified: showInsurersCreatedByClinic ? false : undefined
  });

  return (
    <>
      <AutocompleteFormField
        enablePortal={enablePortal}
        openOnFocus
        name={name}
        label={label}
        isLoading={isLoading}
        multiple={multiple}
        noOptionsText={
          !search?.length
            ? "Search Insurer by Payer ID or Insurer Name..."
            : "No options"
        }
        options={insurers}
        getOptionLabel={(option) => {
          if (typeof option === "string") return option;
          return option?.name ?? "";
        }}
        renderOption={(props: object, option: Insurer) => (
          <PolicyInsurerListItem
            props={props}
            option={option}
            search={search.trim()}
            key={option.id}
            isNew={option.id === -1}
          />
        )}
        isOptionEqualToValue={(option: Insurer, value: Insurer) =>
          option.id === value.id
        }
        popupIcon={<ArrowDropDownIcon />}
        control={control}
        onTextInputChange={(text) => {
          setSearch(text);
        }}
        onOptionSelected={(option: any) => {
          if (onOptionSelected) {
            setSearch(option?.name ?? "");
            onOptionSelected(option);
          }
        }}
        sx={{ ml: 0.5, mt: 0, minWidth: 250, ...sx }}
      />

      <FormControlLabel
        label="Only display insurers clinic has created"
        control={
          <Checkbox
            checked={showInsurersCreatedByClinic}
            onChange={(event) => {
              setShowInsurersCreatedByClinic(event.target.checked);
            }}
          />
        }
        sx={{ mt: 1 }}
      />
    </>
  );
}
